import { DataTable } from '@optools/components/data-table/data-table';
import { DataTableAddButton } from '@optools/components/data-table/data-table-add-button';
import { DataTableColumnHeader } from '@optools/components/data-table/data-table-column-header';
import { DataTableFacet } from '@optools/components/data-table/data-table-facet';
import { Badge } from '@optools/components/ui/badge';
import { Button } from '@optools/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@optools/components/ui/dropdown-menu';
import { useAccessCheck } from '@optools/hooks/useAccessCheck';
import {
  ActionType,
  EsniRule,
  EsniRuleStatus,
  EsniRuleType,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { selectToolsConfig } from '@optools/store/configApiSlice';
import { useAppSelector } from '@optools/store/hooks';
import { useNavigate } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';
import { escapeRegExp, startCase } from 'es-toolkit';
import { CircleDotDashed, FileBraces, Group, MoreHorizontal, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';

import { DeleteEsniRuleDialog } from './delete-esni-rule-dialog';
import { ESNIRuleStatusBadge } from './esni-rule-status-badge';
import { ESNIRuleTypeBadge } from './esni-rule-type-badge';

const FACETS = [
  {
    icon: <CircleDotDashed />,
    key: 'status',
    options: EsniRuleStatus,
    title: 'Status',
  },
  {
    icon: <Group />,
    key: 'type',
    options: EsniRuleType,
    title: 'Type',
  },
  {
    icon: <FileBraces />,
    key: 'action',
    options: ActionType,
    title: 'Action',
  },
] as const;

type ESNIRuleFacetKey = (typeof FACETS)[number]['key'];

export interface ESNIRulesTableProps {
  data: EsniRule[];
}

export function ESNIRulesTable({ data }: ESNIRulesTableProps) {
  const { esni } = useAppSelector(selectToolsConfig);
  const { checkGroupAccess } = useAccessCheck();
  const navigate = useNavigate({ from: '/esni/rules' });

  const [facets, setFacets] = useState<Record<ESNIRuleFacetKey, string[]>>({
    action: [],
    status: [],
    type: [],
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState<EsniRule | undefined>(undefined);

  const handleOnDeleteDialogOpenChange = (open: boolean) => {
    setDeleteDialogOpen(open);
    if (!open) {
      setSelectedRule(undefined);
    }
  };

  const handleResetFacets = () => {
    setFacets({ action: [], status: [], type: [] });
  };

  const canDeleteRule = checkGroupAccess(esni.auth.pages.rules.featureGroups.deleteRule);
  const columns = useMemo<ColumnDef<EsniRule>[]>(() => {
    return [
      {
        accessorKey: 'name',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
        meta: {
          cellClassName: 'w-1/3',
        },
      },
      {
        accessorKey: 'status',
        cell: ({ row }) => <ESNIRuleStatusBadge status={row.original.status} />,
        filterFn: (row, _, filterValue: string[]) => {
          if (!filterValue.length) return true;
          return filterValue.includes(row.original.status);
        },
        header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      },
      {
        accessorKey: 'type',
        cell: ({ row }) => <ESNIRuleTypeBadge type={row.original.type} />,
        filterFn: (row, _, filterValue: string[]) => {
          if (!filterValue.length) return true;
          return !filterValue.length || filterValue.includes(row.original.type);
        },
        header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
      },
      {
        accessorKey: 'priority',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Priority" />,
      },
      {
        accessorFn: (row) => {
          return row.actions
            .map(({ actionType }) => `${actionType} ${startCase(actionType)}`)
            .join(' ');
        },
        accessorKey: 'actions',
        cell: ({ row }) => {
          return (
            <div className="flex">
              {row.original.actions.map(({ actionType }) => (
                <Badge key={actionType} variant="outline">
                  {startCase(actionType)}
                </Badge>
              ))}
            </div>
          );
        },
        filterFn: (row, _, filterValue: string[]) => {
          if (!filterValue.length) return true;
          return filterValue.some((value) => {
            return row.original.actions.some(({ actionType }) => actionType === value);
          });
        },
        header: 'Actions',
        id: 'actions',
        meta: {
          cellClassName: 'w-1/3',
        },
      },
      {
        cell: ({ row }) => {
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon-sm" variant="ghost">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    navigate({
                      params: { ruleId: row.original.id },
                      to: '/esni/rules/$ruleId',
                    });
                  }}>
                  View
                </DropdownMenuItem>
                {canDeleteRule && (
                  <DropdownMenuItem
                    onClick={() => {
                      setDeleteDialogOpen(true);
                      setSelectedRule(row.original);
                    }}>
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        id: 'userActions',
        meta: {
          cellClassName: 'flex justify-center',
        },
      },
      // Hidden columns that we want to be able to search by, but not show in the table
      { accessorKey: 'id', enableHiding: false },
      {
        accessorKey: 'query',
        enableHiding: false,
        filterFn: (row, _, filterValue: string) => {
          const normalizedQuery = escapeRegExp(row.original.query);
          const filter = escapeRegExp(filterValue);
          return new RegExp(filter).test(normalizedQuery);
        },
      },
    ];
  }, [canDeleteRule]);

  return (
    <>
      <DataTable
        className="animate-fade-in"
        columns={columns}
        data={data}
        initialState={{
          columnVisibility: {
            id: false,
            query: false,
          },
        }}
        state={{
          columnFilters: [
            { id: 'status', value: facets.status },
            { id: 'type', value: facets.type },
            { id: 'actions', value: facets.action },
          ],
        }}>
        {checkGroupAccess(esni.auth.pages.rules.featureGroups.createRule) && (
          <DataTableAddButton
            size="sm"
            onClick={() => {
              navigate({ to: '/esni/rules/create' });
            }}>
            Add ESNI Rule
          </DataTableAddButton>
        )}
        {FACETS.map(({ icon, key, options, title }) => {
          return (
            <DataTableFacet
              key={key}
              icon={icon}
              options={Object.keys(options).map((key) => ({ label: key, value: key }))}
              selected={facets[key]}
              title={title}
              onChange={(value) => setFacets((prev) => ({ ...prev, [key]: value }))}
            />
          );
        })}
        {Object.values(facets).some((facet) => facet.length > 0) && (
          <Button className="text-xs" size="sm" variant="outline" onClick={handleResetFacets}>
            <XCircle />
            Reset
          </Button>
        )}
      </DataTable>
      <DeleteEsniRuleDialog
        name={selectedRule?.name ?? ''}
        open={deleteDialogOpen}
        ruleId={selectedRule?.id ?? ''}
        onOpenChange={handleOnDeleteDialogOpenChange}
      />
    </>
  );
}
